import React, { useEffect, useState } from "react";
import classNames from "classnames";
import {
    addMonths,
    addYears,
    format,
    getMonth,
    getYear,
    setMonth as setDateMonth,
    setYear,
    startOfMonth,
} from "date-fns";
import { ArrowDownIcon, LeftArrowIcon, RightArrowIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { MONTH_LABELS } from "./DatePicker.lib";
import styles from "./DatePicker.module.scss";

type CalendarNavigationView = "day" | "month" | "year";

interface DatePickerCalendarNavigationProps {
    defaultMonth: Date;
    minDate?: Date;
    maxDate?: Date;
    numberOfMonths: 1 | 2;
    isPopoverSizeFixed: boolean;
    children: (
        month: Date,
        onMonthChange: (month: Date) => void,
    ) => React.ReactNode;
}

const getYears = (minDate?: Date, maxDate?: Date) => {
    const currentYear = getYear(new Date());
    const firstYear = minDate ? getYear(minDate) : currentYear - 100;
    const lastYear = maxDate ? getYear(maxDate) : currentYear + 20;

    return Array.from(
        { length: Math.max(lastYear - firstYear + 1, 0) },
        (_, index) => firstYear + index,
    );
};

const scrollSelectedYearIntoView = (element: HTMLButtonElement | null) => {
    const list = element?.parentElement;
    if (!element || !list) {
        return;
    }

    const listRect = list.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    list.scrollTop +=
        elementRect.top -
        listRect.top -
        listRect.height / 2 +
        elementRect.height / 2;
};

export const DatePickerCalendarNavigation = ({
    defaultMonth,
    minDate,
    maxDate,
    numberOfMonths,
    isPopoverSizeFixed,
    children,
}: DatePickerCalendarNavigationProps) => {
    const [month, setMonth] = useState(startOfMonth(defaultMonth));
    const [view, setView] = useState<CalendarNavigationView>("day");
    const defaultMonthTime = startOfMonth(defaultMonth).getTime();
    useEffect(() => {
        setMonth(new Date(defaultMonthTime));
    }, [defaultMonthTime]);
    const years = getYears(minDate, maxDate);
    const visibleYear = getYear(month);
    const visibleMonth = getMonth(month);
    const minMonth = minDate ? startOfMonth(minDate) : undefined;
    const maxMonth = maxDate ? startOfMonth(maxDate) : undefined;
    const isDayView = view === "day";
    const lastVisibleMonth = isDayView
        ? addMonths(month, numberOfMonths - 1)
        : month;
    const canGoPrevious = isDayView
        ? !minMonth || month > minMonth
        : !minDate || visibleYear > getYear(minDate);
    const canGoNext = isDayView
        ? !maxMonth || lastVisibleMonth < maxMonth
        : !maxDate || visibleYear < getYear(maxDate);
    const clampVisibleMonth = (candidate: Date): Date => {
        const latestStart = maxMonth
            ? addMonths(maxMonth, -(numberOfMonths - 1))
            : undefined;
        if (minMonth && candidate < minMonth) {
            return minMonth;
        }
        if (latestStart && candidate > latestStart) {
            return latestStart;
        }
        return candidate;
    };
    const goToPrevious = () =>
        setMonth(
            isDayView
                ? clampVisibleMonth(addMonths(month, -numberOfMonths))
                : addYears(month, -1),
        );
    const goToNext = () =>
        setMonth(
            isDayView
                ? clampVisibleMonth(addMonths(month, numberOfMonths))
                : addYears(month, 1),
        );
    const isMonthOutOfRange = (monthIndex: number): boolean => {
        const candidate = new Date(visibleYear, monthIndex, 1);
        return Boolean(
            (minMonth && candidate < minMonth) ||
            (maxMonth && candidate > maxMonth),
        );
    };
    const isYearOutOfRange = (year: number): boolean =>
        Boolean(
            (minDate && year < getYear(minDate)) ||
            (maxDate && year > getYear(maxDate)),
        );

    return (
        <div
            className={classNames(styles.dateSelector, {
                [styles.fixedDateSelector]: isPopoverSizeFixed,
                [styles.twoMonthDateSelector]:
                    isPopoverSizeFixed && numberOfMonths === 2,
            })}
        >
            <div className={styles.dateSelectorHeader}>
                <Button
                    type="button"
                    iconOnly
                    variant="secondary"
                    className={styles.dateNavigationArrow}
                    disabled={!canGoPrevious}
                    onClick={goToPrevious}
                    aria-label="Previous"
                >
                    <LeftArrowIcon />
                </Button>
                <div className={styles.dateNavigationGroup}>
                    <Button
                        type="button"
                        variant="primary"
                        className={styles.dateNavigationMonth}
                        data-active={view === "month" || undefined}
                        onClick={() =>
                            setView(view === "month" ? "day" : "month")
                        }
                        rightIcon={<ArrowDownIcon />}
                    >
                        {format(month, "MMMM")}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className={styles.dateNavigationYear}
                        data-active={view === "year" || undefined}
                        onClick={() =>
                            setView(view === "year" ? "day" : "year")
                        }
                        rightIcon={<ArrowDownIcon />}
                    >
                        {visibleYear}
                    </Button>
                </div>
                <Button
                    type="button"
                    iconOnly
                    variant="secondary"
                    className={styles.dateNavigationArrow}
                    disabled={!canGoNext}
                    onClick={goToNext}
                    aria-label="Next"
                >
                    <RightArrowIcon />
                </Button>
            </div>
            {view === "day" && children(month, setMonth)}
            {view === "month" && (
                <div className={styles.dateMonthGrid}>
                    {MONTH_LABELS.map((monthLabel, monthIndex) => (
                        <button
                            key={monthLabel}
                            type="button"
                            data-selected={
                                monthIndex === visibleMonth || undefined
                            }
                            disabled={isMonthOutOfRange(monthIndex)}
                            onClick={() => {
                                setMonth(setDateMonth(month, monthIndex));
                                setView("day");
                            }}
                        >
                            {monthLabel}
                        </button>
                    ))}
                </div>
            )}
            {view === "year" && (
                <div className={styles.dateYearList}>
                    {years.map((year) => (
                        <button
                            key={year}
                            ref={
                                year === visibleYear
                                    ? scrollSelectedYearIntoView
                                    : undefined
                            }
                            type="button"
                            data-selected={year === visibleYear || undefined}
                            disabled={isYearOutOfRange(year)}
                            onClick={() => {
                                setMonth(setYear(month, year));
                                setView("day");
                            }}
                        >
                            {year}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
